import { rgbToHex } from "./color.js";
import { getSwatchAtPoint, pointerEventToImagePoint } from "./hitTesting.js";
import { loadPaletteImage } from "./imageCache.js";
import { renderPalette } from "./renderPalette.js";
import type { PalettePickerOptions, PaletteSwatchColors, PaletteTemplate, Swatch } from "./types.js";
import { validateImageDimensions, validatePaletteTemplate, validateSwatchColors } from "./validatePalette.js";

const sampleContextCache = new Map<string, CanvasRenderingContext2D | null>();

function sameSwatchColors(a?: PaletteSwatchColors, b?: PaletteSwatchColors): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[Number(key)] !== b[Number(key)]) return false;
  }
  return true;
}

export class PalettePicker {
  private readonly canvas: HTMLCanvasElement;
  private readonly template: PaletteTemplate;
  private readonly onSelect: PalettePickerOptions["onSelect"];
  private readonly onSample: PalettePickerOptions["onSample"];
  private readonly onError: PalettePickerOptions["onError"];
  private image: HTMLImageElement | null = null;
  private originalCtx: CanvasRenderingContext2D | null = null;
  private destroyed = false;
  private texture: boolean;
  private debug: boolean;
  private selectedIndex: number | null;
  private selectedHex: string | null = null;
  private swatchColors: PaletteSwatchColors | undefined;
  private draggingPointerId: number | null = null;
  private pendingDragEvent: PointerEvent | null = null;
  private dragFrame: number | null = null;
  private readonly handlePointerDown = (event: PointerEvent) => this.onPointerDown(event);
  private readonly handlePointerMove = (event: PointerEvent) => this.onPointerMove(event);
  private readonly handlePointerUp = (event: PointerEvent) => this.onPointerUp(event);

  constructor(options: PalettePickerOptions) {
    this.canvas = options.canvas;
    this.template = options.template;
    this.texture = options.texture ?? false;
    this.debug = options.debug ?? false;
    this.selectedIndex = options.selectedIndex ?? null;
    this.swatchColors = options.swatchColors;
    this.selectedHex = this.selectedIndex === null ? null : this.colorForSwatchIndex(this.selectedIndex);
    this.onSelect = options.onSelect;
    this.onSample = options.onSample;
    this.onError = options.onError;
    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("pointercancel", this.handlePointerUp);
  }

  async load(): Promise<void> {
    try {
      validatePaletteTemplate(this.template);
      validateSwatchColors(this.template, this.swatchColors);
      const image = await loadPaletteImage(this.template.imageUrl);
      // A destroyed picker must not paint over its successor's shared canvas.
      if (this.destroyed) return;
      validateImageDimensions(this.template, image);
      this.image = image;
      this.originalCtx = sampleContextCache.get(this.template.imageUrl) ?? null;
      this.render();
      this.prepareSamplingContextWhenIdle();
    } catch (error) {
      if (this.destroyed) return;
      const err = error instanceof Error ? error : new Error(String(error));
      this.onError?.(err);
      throw err;
    }
  }

  render(): void {
    if (!this.image) return;
    renderPalette({
      canvas: this.canvas,
      image: this.image,
      template: this.template,
      texture: this.texture,
      debug: this.debug,
      swatchColors: this.swatchColors,
    });
  }

  setTexture(enabled: boolean): void {
    this.texture = enabled;
    this.render();
  }

  setDebug(enabled: boolean): void {
    this.debug = enabled;
    this.render();
  }

  setSelectedIndex(index: number | null): void {
    this.selectedIndex = index;
    this.selectedHex = index === null ? null : this.colorForSwatchIndex(index);
  }

  setSwatchColors(swatchColors?: PaletteSwatchColors): void {
    validateSwatchColors(this.template, swatchColors);
    if (sameSwatchColors(this.swatchColors, swatchColors)) return;
    this.swatchColors = swatchColors;
    this.selectedHex = this.selectedIndex === null ? null : this.colorForSwatchIndex(this.selectedIndex);
    this.render();
  }

  setMarkers(): void {
    // Markers are drawn by the lightweight React overlay.
  }

  destroy(): void {
    this.destroyed = true;
    if (this.dragFrame !== null) window.cancelAnimationFrame(this.dragFrame);
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointercancel", this.handlePointerUp);
  }

  private onPointerDown(event: PointerEvent): void {
    if (!this.image) return;
    this.draggingPointerId = event.pointerId;
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
    this.pickFromEvent(event);
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.draggingPointerId !== event.pointerId) return;
    event.preventDefault();
    this.pendingDragEvent = event;
    if (this.dragFrame !== null) return;
    this.dragFrame = window.requestAnimationFrame(() => {
      this.dragFrame = null;
      const pending = this.pendingDragEvent;
      this.pendingDragEvent = null;
      if (pending) this.pickFromEvent(pending);
    });
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.draggingPointerId !== event.pointerId) return;
    this.draggingPointerId = null;
    this.pendingDragEvent = null;
    if (this.dragFrame !== null) {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
    }
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  }

  private pickFromEvent(event: PointerEvent): void {
    if (!this.image) return;
    const point = pointerEventToImagePoint(event, this.canvas);
    const swatch = getSwatchAtPoint(point.x, point.y, this.template.swatches);
    if (swatch) {
      const hex = this.colorForSwatch(swatch);
      if (this.selectedIndex === swatch.index && this.selectedHex?.toLowerCase() === hex.toLowerCase()) return;
      this.selectedIndex = swatch.index;
      this.selectedHex = hex;
      this.onSelect?.({ swatch: { ...swatch, baseHex: hex }, hex, index: swatch.index });
      return;
    }
    const hex = this.sampleOriginal(point.x, point.y);
    if (hex) {
      this.selectedIndex = null;
      this.selectedHex = null;
      this.onSample?.({ swatch: null, hex, x: point.x, y: point.y });
    }
  }

  private sampleOriginal(x: number, y: number): string | null {
    const ctx = this.originalCtx ?? this.createSamplingContext();
    if (!ctx || x < 0 || y < 0 || x >= this.template.imageWidth || y >= this.template.imageHeight) return null;
    const data = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
    return rgbToHex(data[0] ?? 0, data[1] ?? 0, data[2] ?? 0);
  }

  private prepareSamplingContextWhenIdle(): void {
    const prepare = () => {
      if (!this.destroyed && this.image && !sampleContextCache.has(this.template.imageUrl)) this.createSamplingContext();
    };
    const requestIdle = window.requestIdleCallback;
    if (typeof requestIdle === "function") {
      requestIdle.call(window, prepare, { timeout: 1000 });
    } else {
      globalThis.setTimeout(prepare, 0);
    }
  }

  private createSamplingContext(): CanvasRenderingContext2D | null {
    const cached = sampleContextCache.get(this.template.imageUrl);
    if (cached !== undefined) {
      this.originalCtx = cached;
      return cached;
    }
    if (!this.image) return null;
    const originalCanvas = document.createElement("canvas");
    originalCanvas.width = this.template.imageWidth;
    originalCanvas.height = this.template.imageHeight;
    const ctx = originalCanvas.getContext("2d", { willReadFrequently: true });
    ctx?.drawImage(this.image, 0, 0, this.template.imageWidth, this.template.imageHeight);
    sampleContextCache.set(this.template.imageUrl, ctx);
    this.originalCtx = ctx;
    return ctx;
  }

  private colorForSwatch(swatch: Swatch): string {
    return this.swatchColors?.[swatch.index] ?? swatch.baseHex;
  }

  private colorForSwatchIndex(index: number): string | null {
    const swatch = this.template.swatches.find((item) => item.index === index);
    return swatch ? this.colorForSwatch(swatch) : null;
  }
}
